import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaBanner } from "@/components/CtaBanner";
import { blogPosts, getBlogPost, SITE_URL } from "@/data/seo";

const formatDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export const BlogIndex = () => (
  <main className="bg-white min-h-screen">
    <Seo
      title="Moving & Cleaning Blog — Tips for Cardiff & South Wales | BoxB Solutions"
      description="Guides, checklists and pricing insights for house moves, office relocations, packing and end of tenancy cleaning across Cardiff and South Wales."
      canonical="/blog"
    />
    <Nav />
    <section className="pt-28 md:pt-32 pb-16">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Breadcrumbs trail={[
          { href: "/", label: "Home" },
          { href: "/blog", label: "Blog" },
        ]} />
        <h1 className="mt-8 font-display font-bold text-[#0A192F] text-4xl sm:text-5xl tracking-tight">Moving &amp; cleaning insights</h1>
        <p className="mt-5 text-lg text-slate-600">Straight-talking guides from the BoxB Solutions team.</p>

        <div className="mt-12 space-y-4">
          {blogPosts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                  className="block rounded-3xl border border-slate-200 bg-white p-7 hover-lift hover:border-[#0A192F] transition-colors">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Calendar className="h-3.5 w-3.5" /> {formatDate(p.date)}
              </div>
              <h2 className="mt-3 font-display font-semibold text-2xl text-[#0A192F]">{p.title}</h2>
              <p className="mt-2 text-slate-600 line-clamp-2">{p.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0A192F]">
                Read post <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
    <CtaBanner />
    <Footer />
    <StickyCTA />
  </main>
);

export const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const canonical = `/blog/${post.slug}`;
  const jsonld = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: "BoxB Solutions" },
    publisher: { "@type": "Organization", name: "BoxB Solutions", logo: { "@type": "ImageObject", url: `${SITE_URL}/og-cover.svg` } },
    mainEntityOfPage: `${SITE_URL}${canonical}`,
    keywords: post.tags.join(", "),
  };

  return (
    <main className="bg-white min-h-screen">
      <Seo title={`${post.title} | BoxB Solutions`} description={post.excerpt} canonical={canonical} jsonld={jsonld} />
      <Nav />
      <article className="pt-28 md:pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Breadcrumbs trail={[
            { href: "/", label: "Home" },
            { href: "/blog", label: "Blog" },
            { href: canonical, label: post.title },
          ]} />
          <div className="mt-6 flex items-center gap-2 text-xs text-slate-500 uppercase tracking-widest font-bold">
            <Calendar className="h-3.5 w-3.5" /> {formatDate(post.date)}
          </div>
          <h1 className="mt-3 font-display font-bold text-[#0A192F] text-4xl sm:text-5xl tracking-tight leading-[1.1]">
            {post.title}
          </h1>
          <p className="mt-5 text-lg text-slate-600">{post.excerpt}</p>

          <div className="mt-10 space-y-5 text-slate-700 text-lg leading-relaxed">
            {post.body.map((block, i) => {
              const blockKey = `${block.type}-${i}`;
              if (block.type === "p") return <p key={blockKey}>{block.text}</p>;
              if (block.type === "h2") return <h2 key={blockKey} className="font-display font-semibold text-2xl sm:text-3xl text-[#0A192F] tracking-tight mt-10">{block.text}</h2>;
              if (block.type === "ul") return (
                <ul key={blockKey} className="list-disc pl-6 space-y-2">
                  {block.items.map((it) => <li key={it}>{it}</li>)}
                </ul>
              );
              return null;
            })}
          </div>

          <div className="mt-14 rounded-3xl bg-[#F8FAFC] border border-slate-200 p-6 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="inline-flex items-center rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-medium text-[#0A192F]">{t}</span>
            ))}
          </div>
        </div>
      </article>
      <CtaBanner />
      <Footer />
      <StickyCTA />
    </main>
  );
};
